import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Btn } from "@/ui/btn/Btn";
import { CheckboxIcon } from "@/ui/icon/icons/checkbox-icon/CheckboxIcon";
import { stopPropagationEventHandler } from "@/utils/dom";
import { getCurrencyParts } from "@/utils/number";
import { useCalculator } from "../../hooks/useCalculator";
import { useCalculatorUpdate } from "../../hooks/useCalculatorUpdate";
import type { Calc } from "../../types";
import { CalculatorCounter } from "../calculator-counter/CalculatorCounter";
import { CalculatorSelect } from "../calculator-select/CalculatorSelect";
import styles from "./CalculatorModule.module.css";

export const CalculatorModule: React.FC<{
  module: Calc.Module.Module;
}> = ({ module }) => {
  const moduleState = useCalculator((state) => state.modules.get(module.id));
  const setModuleActive = useCalculator((state) => state.setModuleActive);
  const setModuleValue = useCalculator((state) => state.setModuleValue);
  const setModuleConfirmed = useCalculator((state) => state.setModuleConfirmed);
  const getActiveModulesCount = useCalculator(
    (state) => state.getActiveModulesCount,
  );
  const [isDisableConfirmActive, setDisableConfirmActive] = useState(false);
  const isDisableConfirmRequired =
    moduleState?.isDisableConfirmRequired ?? false;
  const active = moduleState?.active ?? module.active;

  const onModuleToggle = () => {
    if (active === false) {
      setModuleActive(module.id, true);

      if (module.type === "counter") {
        setModuleValue(
          module.id,
          module.counter.id,
          module.counter.defaultValue,
        );
      }
    } else if (isDisableConfirmRequired === true) {
      if (getActiveModulesCount() > 1) {
        setDisableConfirmActive(true);
      }
    } else if (getActiveModulesCount() > 1) {
      setModuleActive(module.id, false);

      if (module.type === "counter") {
        setModuleValue(module.id, module.counter.id, 0);
      }

      if (module.type === "select") {
        for (const select of module.select) {
          setModuleValue(module.id, select.id, select.defaultValue);
        }
      }
    }
  };

  const onCounterChange = (value: number) => {
    if (module.type === "counter") {
      if (value === 0 && isDisableConfirmRequired === true) {
        if (getActiveModulesCount() > 1) {
          setDisableConfirmActive(true);
        }
      } else if (value > 0 || getActiveModulesCount() > 1) {
        setModuleActive(module.id, value > 0);
        setModuleValue(module.id, module.counter.id, value);
      }
    }
  };

  const onDisableConfirmSubmit = async () => {
    setModuleConfirmed(module.id);
    setDisableConfirmActive(false);

    if (getActiveModulesCount() > 1) {
      setModuleActive(module.id, false);

      if (module.type === "counter") {
        setModuleValue(module.id, module.counter.id, 0);
      }

      if (module.type === "select") {
        for (const select of module.select) {
          setModuleValue(module.id, select.id, select.defaultValue);
        }
      }
    }
  };

  const onConfirmCancel = () => {
    setDisableConfirmActive(false);
  };

  const { data } = useCalculatorUpdate();

  const price =
    data.modules.find((m) => m.id === module.id)?.price ?? module.price;

  return (
    <div
      className={clsx(styles.module, [
        active && styles.active,
        module.type === "counter" && styles["with-counter"],
        module.type === "select" && active && styles["with-select"],
      ])}
      onClick={onModuleToggle}
    >
      <div className={styles.header}>
        <CheckboxIcon active={active} />
        <div className={styles.info}>
          <div className={styles.title}>{module.title}</div>
          <div className={styles.text}>{module.text}</div>
        </div>
      </div>

      {module.type === "counter" && (
        <CalculatorCounter
          className={styles.counter}
          value={
            moduleState?.values.get(module.counter.id) ??
            module.counter.defaultValue
          }
          counter={module.counter}
          disabled={active === false}
          onChange={onCounterChange}
        />
      )}

      {module.type === "select" && active && (
        <div className={styles.selectList}>
          {module.select.map((select) => (
            <CalculatorSelect
              key={select.id}
              className={styles.select}
              moduleId={module.id}
              select={select}
            />
          ))}
        </div>
      )}

      <ModulePrice {...price} />

      {isDisableConfirmActive && (
        <ModuleConfirm
          onSubmit={onDisableConfirmSubmit}
          onCancel={onConfirmCancel}
        />
      )}
    </div>
  );
};

const ModulePrice: React.FC<Calc.Price.Price> = ({ value, currency }) => {
  const parts = getCurrencyParts(value, currency);

  return (
    <div className={styles.price}>
      <div className={styles.currency}>{parts.currency}</div>
      <div className={styles.value}>
        {parts.plusSign}
        {parts.minusSign}
        <div className={styles.integer}>{parts.integer}</div>
        <div className={styles.decimal}>{parts.decimal}</div>
        <div className={styles.fraction}>{parts.fraction}</div>
      </div>
    </div>
  );
};

const ModuleConfirm: React.FC<{
  onSubmit?: () => void;
  onCancel?: () => void;
}> = ({ onSubmit, onCancel }) => {
  const { t } = useTranslation();

  return (
    <div className={styles.confirm} onClick={stopPropagationEventHandler()}>
      <div className={styles.confirmTitle}>{t("Confirm removal")}</div>
      <Btn icon="cross_16px" mode="remove" size="sm" onClick={onCancel} />
      <Btn icon="check_mark_16px" mode="confirm" size="sm" onClick={onSubmit} />
    </div>
  );
};
